import React, { Fragment, useEffect, useState } from "react";
import { Controller, useForm } from "react-hook-form";
import { DropdownList } from "react-widgets";
import { toastr } from "react-redux-toastr";
import moment from "moment";
import api from "../../../../api";
import { AutoCompleteInput } from "../../../../components/form-group";
import ErrorMessage from "../../../../components/text/ErrorMessage";

import {
  IAddLeadActivityModel,
  VehicleModelMinimal,
  VehicleVariantMinimal,
  Lead,
  Customer,
  capitalizeFirstLetter,
} from "../../../../core";
import { useLeadActivities } from "../../../../core/hooks/useLeadActivities";
import { combineDateAndTime } from "../../../../utils/CombineDateAndTime";
import LeadActivityAutoCompleteInput from "../../../../components/form-group/lead-activity-autocomplete-input";
import { ITimePeriodData } from "../../../../core/components/time-period/custom-time-period";
import {
  isTimePeriodValid,
  SingleDayTimePeriod,
} from "../../../../core/components/time-period/single-day-time-period";
import classnames from "classnames";
import { useAppSettings } from "../../../../core/hooks/useAppSettings";
import { checkIfExcludedDatesBeBlocked } from "../../../../core/utils/event-utils";
import SalesPersonsAutoCompleteInput from "../../../../components/form-group/sales-persons-auto-complete-input";
import RemoteDataMultiSelectDropDown from "../../../../components/dropdown/RemoteDataMultiSelectDropDown";
import { UserImage } from "../../../../components/user-image";

interface IAddActivityProps {
  leadId?: string;
  activity?: ActivityModel;
  onSubmit?: (activity: ActivityModel) => void;
  onCancel?: () => void;
  leads?: Lead[];
  isEdit?: boolean;
  onSubmitCompleted?: () => void;
  onDateChange?: (date: Date) => void;
  alwaysShowform?: boolean;
  onSuccess?: () => void;
  onChangeAssignee?: (id: string) => void;
  onTimePeriodChange?: (data?: ITimePeriodData) => void;
  excludedDates?: Date[];
  onActivityTypeChange?: (type: string) => void;
  companyId?: string;
  activityCategory?: string;
  activityCategoryId?: string
  customerId?: string
  isCustomerActivity?: boolean | undefined
}

export const AddActivityWraper: React.FunctionComponent<IAddActivityProps> = (
  props
) => {
  const { addLeadActivity } = useLeadActivities(api.lead);
  const [showEditor, setShowEditor] = useState(false);
  const alwaysShowform = props.alwaysShowform ?? false;

  const onSubmit = async (data: ActivityModel) => {
    const startDate = combineDateAndTime(
      data.timePeriod?.startDate!,
      data.timePeriod?.startTime!
    );
    const endDate = combineDateAndTime(
      data.timePeriod?.startDate!,
      data.timePeriod?.endTime!
    );

    const salesPerson = data?.salesPerson?.length ? data.salesPerson?.map((item:any)=>item._id) :data.salesPerson._id
    
    const apiModel: IAddLeadActivityModel = {
      type: data.type!,
      title: data.title!,
      startDate,
      endDate,
      salesPerson,
      // leadId: data.leadId as any,
      note: data.note,
      ...(props.isCustomerActivity
        ? { activityCategory:  'customer' }
        : {  }),
      ...(props.companyId
        ? { companyId: props.companyId }
        : { leadId: data.leadId as any }),
      ...(props.activityCategory && props.activityCategoryId
        ? {
           activityCategory: props.activityCategory,
           [`${props.activityCategory}Id`]: props.activityCategoryId
         }
        : {  }),
    };
    const forAll = props.leads && props.leads?.length > 0 ? true : false;
    addLeadActivity(
      apiModel,
      () => {
        toastr.success("Success", "Activity added");
        setShowEditor(false);
        if (props.onSuccess) {
          props.onSuccess();
        }
        props.onSubmitCompleted?.();
      },
      forAll
    );
  };

  return (
    <Fragment>
      {!showEditor && !alwaysShowform && (
        <div
          onClick={() => {
            setShowEditor(true);
          }}
          className="border-bottom p-4 text-gray-400 font-xs"
        >
          Click here to add activity...
        </div>
      )}
      {(showEditor || alwaysShowform) && (
        <div className={classnames({ "px-4 ": !alwaysShowform, "mt-4": true })}>
          <AddActivityForm
            leadId={props.leadId}
            onDateChange={props.onDateChange}
            activity={props.activity}
            onChangeAssignee={(id: string) => {
              if (props.onChangeAssignee) {
                props.onChangeAssignee(id);
              }
            }}
            onCancel={() => {
              setShowEditor(false);
              if (props.onCancel) {
                props.onCancel();
              }
            }}
            onSuccess={() => {
              setShowEditor(false);
              if (props.onSuccess) {
                props.onSuccess();
              }
            }}
            onSubmit={(activity: ActivityModel) => {
              onSubmit(activity);
            }}
            leads={props.leads}
          ></AddActivityForm>
        </div>
      )}
    </Fragment>
  );
};

export const AddActivity: React.FunctionComponent<IAddActivityProps> = (
  props
) => {
  const [showEditor, setShowEditor] = useState(false);

  return (
    <Fragment>
      {!showEditor && (
        <div
          onClick={() => {
            setShowEditor(true);
          }}
          className="border-bottom p-4 text-gray-400 font-xs"
        >
          Click here to add activity...
        </div>
      )}
      {showEditor && (
        <div>
          <AddActivityForm
            leadId={props.leadId}
            onDateChange={props.onDateChange}
            activity={props.activity}
            onCancel={() => {
              setShowEditor(false);
            }}
            onSubmitCompleted={() => {
              setShowEditor(false);
            }}
            onSuccess={() => {
              setShowEditor(false);
            }}
            leads={props.leads}
          ></AddActivityForm>
        </div>
      )}
    </Fragment>
  );
};

export const AddActivityForm: React.FunctionComponent<IAddActivityProps> = (
  props
) => {
  const appSettings = useAppSettings(api.settings);
  const { getActivityIcon } = useLeadActivities(api.lead);
  const activity = props.activity;
  const { control, handleSubmit, errors, setValue } = useForm<ActivityModel>({
    reValidateMode: "onChange",
    mode: "onSubmit",
  });

  const buttons: IconButtonModel[] = [
    { placeholder: "Call", type: "call" },
    { placeholder: "Meeting", type: "meeting" },
    // { placeholder: "Deadline", type: "deadline" },
    { placeholder: "Task", type: "task" },
    // { placeholder: "Phone In", type: "phoneIn" },
    // { placeholder: "Phone Out", type: "phoneOut" },
    // { placeholder: "Email In", type: "emailIn" },
    // { placeholder: "Email Out", type: "emailOut" },
    { placeholder: "Email", type: "emailOut" },
    // { placeholder: "Letter In", type: "letterIn" },
    // { placeholder: "Letter Out", type: "letterOut" },
    // { placeholder: "Fax In", type: "faxIn" },
    // { placeholder: "Fax Out", type: "faxOut" },
    { placeholder: "Cold Calling", type: "coldCalling" },
    // { placeholder: "To Do", type: "toDo" },
  ];
  const [selectedType, setSelectedType] = useState<string>();
  useEffect(() => {
    if (props.activity?.type != null) {
      setSelectedType(props.activity?.type);
    } else {
      setSelectedType(buttons[0].type);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  const getSelectedObject = (): IconButtonModel => {
    if (selectedType != null) {
      const obj = buttons.find((p) => p.type === selectedType);
      if (obj != null) {
        return obj;
      }
    }
    return buttons[0];
  };
  useEffect(() => {
    if (selectedType && props.onActivityTypeChange) {
      props.onActivityTypeChange(selectedType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedType]);
  const { addLeadActivity, updating } = useLeadActivities(api.lead);

  const [isSalesPersonChanging, setSalesPersonChanging] = useState(false);

  const onSubmit = async (data: ActivityModel) => {
    if (!props.isEdit) {
      const startDate = combineDateAndTime(
        data.timePeriod?.startDate!,
        data.timePeriod?.startTime!
      );
      const endDate = combineDateAndTime(
        data.timePeriod?.startDate!,
        data.timePeriod?.endTime!
      );
      
    const salesPerson = data?.salesPerson?.length? data.salesPerson?.map((item:any)=>item._id) : data.salesPerson._id

    let companyPeople = []
    if(data?.companyPeople?.length){
      companyPeople = data.companyPeople?.map((item:any)=>({firstName: item?.firstName, peopleID:item?.peopleID, _id:item?._id, lastName: item?.lastName}))
    }
    
      const apiModel: IAddLeadActivityModel = {
        type: selectedType!,
        title: data.title!,
        startDate,
        endDate,
        salesPerson,
        companyPeople,
        ...(props.isCustomerActivity
          ? { activityCategory:  'customer' }
          : {  }),
        ...(props.companyId
          ? { companyId: props.companyId }
          : { leadId: data.leadId as any }),
          ...(props.activityCategory && props.activityCategoryId
            ? { 
              activityCategory: props.activityCategory,
              [`${props.activityCategory}Id`]: props.activityCategoryId
            }
            : {  }),
            ...(props.customerId ?  {
              customerId : props.customerId,
              leadId : props.leadId
            } :
              {}),
        note: data.note,
        busyEvent: data.busyEvent,
      };
      const forAll = props.leads && props.leads?.length > 0 ? true : false;
      addLeadActivity(
        apiModel,
        () => {
          toastr.success("Success", "Activity added");
          if (props.onSuccess) {
            props.onSuccess();
          }
          props.onSubmitCompleted?.();
        },
        forAll
      );
    } else {
      if (props.onSubmit) {
        data.type = selectedType as any;
        props.onSubmit(data);
      }
    }
  };

  const renderLeadOption = () => {
    if (props.leadId) {
      return (
        <Controller
          control={control}
          rules={{ required: "Lead is Required" }}
          name="leadId"
          defaultValue={props.leadId}
          render={() => <></>}
        />
      );
    }
    return (
      <div className="form-group font-weight-semibold mb-3">
        <Controller
          control={control}
          // rules={{ required: "Lead is required" }}
          name="leadId"
          defaultValue={props.leads?.[0]._id}
          render={({ onChange, value, name }) => (
            <LeadActivityAutoCompleteInput
              defaultValue={value}
              placeholder={"LEAD / DEAL"}
              onChange={(val) => {
                onChange(val._id);
                setValue(
                  "salesPerson",
                  val?.currentSalesPerson?._id || undefined
                );
              }}
              list={props.leads}
            />
          )}
        />
        <div>
          <ErrorMessage>{errors.leadId?.message}</ErrorMessage>
        </div>
      </div>
    );
  };

  const resetEndTime = (startTime: string, endTime?: string) => {
    if (endTime) {
      const beginningTime = moment(startTime, "h:mma");
      const endingTime = moment(endTime, "h:mma");
      if (beginningTime.isSameOrAfter(endingTime)) {
        setValue("endTime", "");
      }
    }
  };

  let minDate: any;
  if (
    appSettings.data?.leadActivityAssignment
      ?.noOfDaysToAllowBackdatedActivity !== undefined &&
    appSettings.data?.leadActivityAssignment
      ?.noOfDaysToAllowBackdatedActivity >= 0
  ) {
    minDate = new Date().setDate(
      new Date().getDate() -
      appSettings.data?.leadActivityAssignment
        ?.noOfDaysToAllowBackdatedActivity
    );
  }

  const loadUsers = async () => {
    const response = await api.customer.searchStaff({
      hideBlocked: true,
    });

    return response;
  };

  const loadCompanyPeople = async () => {
    if(props.activityCategory === 'property' || activity?.activityCategory === 'property'){
      const type = 'doc'
      const response = await api.sitevisit.getLandLords(
       props.activityCategoryId || activity?.activityCategoryId, type
      );
      // return response;
      const formattedResponse = response.map(item=>({
        _id : item.customerId,
        firstLastName: item.contactName,
        firstName: item.contactName,
        lastName: ''
      }))
      return formattedResponse;
    }else{
      const response = await api.customer.getCompanyPeople(activity?.company?._id || props.companyId || props?.activity?.companyId);
      return response;
    }
  };

  useEffect(() => {
    if(selectedType && !activity?.title){
      if(selectedType === 'emailOut'){
        setValue(
          "title",
          'Email'
        );
      }else if(selectedType === 'coldCalling'){
        setValue(
          "title",
          'Cold Calling'
        );
      }else{
        setValue(
          "title",
          capitalizeFirstLetter(selectedType)
        );
      }
    }
  }, [selectedType])

  return (
    <Fragment>
      <div
        className="tab-pane fade  show active"
        id="activity-all"
        role="tabpanel"
        aria-labelledby="activity-tab"
      >
        <div className="mt-4">
          <div className="form-group font-weight-semibold mb-3">
            <Controller
              control={control}
              rules={{ required: true }}
              name="title"
              defaultValue={activity?.title || 'Call'}
              render={({ onChange, value, name }) => (
                <input
                  type="text"
                  className="form-control rounded-sm font-weight-semibold"
                  value={value}
                  onChange={onChange}
                  placeholder={getSelectedObject().placeholder}
                />
              )}
            />
            {errors.title && <ErrorMessage>Title is required</ErrorMessage>}
          </div>

          <div className="input-group col-10 px-0 input-group-box mb-3">
            {buttons.map((p, index) => {
              return (
                <button
                  onClick={() => {
                    setSelectedType(p.type);
                  }}
                  className="form-control p-0"
                >
                  <i
                    data-toggle="tooltip"
                    title={p.placeholder}
                    className={
                      getActivityIcon(p.type) +
                      " " +
                      (p.type === selectedType ? "text-primary" : "")
                    }
                  ></i>
                </button>
              );
            })}
          </div>
          <form id={"activity-form"} onSubmit={handleSubmit(onSubmit)}>
            <div className="form-group font-weight-semibold mb-3">
              <label htmlFor="" className="font-sm text-uppercase">
                DATE AND TIME<span className="text-danger">*</span>
              </label>
              <div key={JSON.stringify(activity?.timePeriod ?? {})}>
                <Controller
                  control={control}
                  rules={{ required: true }}
                  name="timePeriod"
                  defaultValue={activity?.timePeriod}
                  render={({ onChange, value, name }) => (
                    <SingleDayTimePeriod
                      onChange={(val: ITimePeriodData) => {
                        if (isTimePeriodValid(val)) {
                          onChange(val);
                          if (props.onTimePeriodChange) {
                            props.onTimePeriodChange(val);
                          }
                        } else {
                          onChange(undefined);
                          if (props.onTimePeriodChange) {
                            props.onTimePeriodChange(undefined);
                          }
                        }
                      }}
                      defaultValue={activity?.timePeriod}
                      minDate={minDate}
                      appointmentTime={
                        appSettings?.data?.application?.appointmentTime
                      }
                      range={"8-8"}
                      excludeDates={
                        checkIfExcludedDatesBeBlocked(selectedType)
                          ? props.excludedDates?.map((date) => new Date(date))
                          : []
                      }
                    ></SingleDayTimePeriod>
                  )}
                />
              </div>
              {errors.timePeriod && (
                <ErrorMessage>Please enter a valid time period</ErrorMessage>
              )}
            </div>

            <div className="form-group font-weight-semibold mb-3">
              <label htmlFor="" className="font-sm text-uppercase">
                COMMENTS
              </label>
              <Controller
                control={control}
                name="note"
                defaultValue={activity?.note}
                render={({ onChange, value, name }) => (
                  <textarea
                    rows={4}
                    className="form-control rounded-sm font-weight-semibold"
                    value={value}
                    onChange={onChange}
                    placeholder={"Comments"}
                  />
                )}
              />
              {errors.note && <ErrorMessage>Comment is required</ErrorMessage>}
            </div>
            <div
              key={activity?.salesPerson?.length ? activity?.salesPerson?.map((item:any)=>item?._id)?.toString() : activity?.salesPerson?._id ?? "" + "other-action-salesman"}
              className="form-group font-weight-semibold mb-3"
            >
              <label htmlFor="" className="font-sm text-uppercase">
                User<span className="text-danger">*</span>
              </label>
              {/* <Controller
                control={control}
                // rules={{ required: true }}
                name="salesPerson"
                defaultValue={activity?.salesPerson}
                render={({ onChange, value, name }) => (
                  <SalesPersonsAutoCompleteInput
                    loadingText={isSalesPersonChanging ? "Assigning ..." : ""}
                    defaultValue={value}
                    placeholder={"User"}
                    onChange={(val) => {
                      onChange(val);
                      if (props.onChangeAssignee) {
                        props.onChangeAssignee(val._id!);
                      }
                    }}
                    valueField={"name"}
                    dataLoader={(query?: string) => {
                      return api.customer.searchStaff({
                        query,
                        hideBlocked: true,
                      });
                    }}
                    disabled={updating}
                    hideBlocked={true}
                  />
                  // <AutoCompleteInput
                  //   defaultValue={value}
                  //   placeholder={'Agent'}
                  //   onChange={(currentValue) => {
                  //     onChange(currentValue);
                  //     if (props.onChangeAssignee) {
                  //       props.onChangeAssignee(currentValue._id!);
                  //     }
                  //   }}
                  //   apiUrl="api/user/sales-persons"
                  //   dataLoader={(query?: string) => {
                  //     return api.customer.searchSalesPerson(
                  //       {
                  //         query,
                  //         hideBlocked: true,
                  //       },
                  //       'other-activity'
                  //     );
                  //   }}
                  //   valueField={'name'}
                  // />
                )}
              /> */}
              <Controller
                control={control}
                rules={{ 
                  required: true,
                  validate: (value) => value && value.length > 0
                 }}
                name="salesPerson"
                defaultValue={activity?.salesPerson&&[activity?.salesPerson]}
                render={({ onChange, value, name }) => (
                  <RemoteDataMultiSelectDropDown
                    loadData={loadUsers}
                    textField="name"
                    value={value}
                    defaultValue={value}
                    valueField="_id"
                    onChange={(val) => {
                      onChange(val);
                      if (props.onChangeAssignee) {
                        props.onChangeAssignee(val._id!);
                      }
                    }}
                    placeholder="Select Users"
                    itemComponent={({ item }) => (
                      <UserImage
                        user={item}
                      ></UserImage>
                    )}
                    tagComponent={({ item }) => (
                      <UserImage
                        user={item}
                      ></UserImage>
                    )}
                />
                )}
              />
              {errors.salesPerson && (
                <ErrorMessage>User is required</ErrorMessage>
              )}
            </div>
            {(props.activityCategoryId || (!activity?.company?._id && !props.activity?.companyId && !props.companyId && !props.leadId) || (activity?.activityCategory === 'project')) && (props?.activityCategory !== 'property' && activity?.activityCategory !== 'property') ? null : <div
              key={"company people"}
              className="form-group font-weight-semibold mb-3"
            >
              <label htmlFor="" className="font-sm text-uppercase">
                People
              </label>
              <Controller
                control={control}
                // rules={{ required: true }}
                name="companyPeople"
                defaultValue={activity?.companyPeople|| []}
                render={({ onChange, value, name }) => (
                  <RemoteDataMultiSelectDropDown
                    loadData={loadCompanyPeople}
                    textField="firstLastName"
                    value={value}
                    defaultValue={value}
                    valueField="_id"
                    onChange={(val) => {
                      onChange(val); 
                    }}
                    placeholder="Select People"
                    itemComponent={({ item }) => (
                      <UserImage
                        user={item}
                        showInfo={!!activity?.company?.name || !!props.companyId || !!props.activity?.companyId}
                      ></UserImage>
                    )}
                    tagComponent={({ item }) => (
                      <UserImage
                        user={item}
                      ></UserImage>
                    )}
                />
                )}
              />
              {errors.companyPeople && (
                <ErrorMessage>Company People is required</ErrorMessage>
              )}
            </div>}
            {/* <div className="form-group font-weight-semibold mb-3">
              <label htmlFor="" className="font-sm text-uppercase">
                TYPE
              </label>
              <Controller
                control={control}
                name="busyEvent"
                defaultValue={activity?.busyEvent || true}
                render={({ onChange, value, name }) => (
                  <DropdownList
                    value={value}
                    data={[
                      { label: "Busy", value: true },
                      // { label: 'Free', value: false },
                    ]}
                    textField={"label"}
                    valueField={"value"}
                    placeholder={"Select Type"}
                    onChange={(val) => onChange(val.value)}
                    disabled
                  />
                )}
              />
            </div> */}
          </form>
          <div className="text-right">
            {(props.activityCategoryId || props.customerId || (activity?.activityCategory === 'project' || activity?.activityCategory === 'property' ||  activity?.activityCategory === 'customer')) ? null :  !props.companyId && renderLeadOption()}
            <button
              onClick={() => {
                if (props.onCancel) {
                  props.onCancel();
                }
              }}
              className="btn btn-outline-dark py-2 font-weight-semibold font-normal px-4 mr-1"
              disabled={updating}
            >
              Cancel
            </button>
            <button
              disabled={updating}
              type="submit"
              form="activity-form"
              className="btn btn-primary py-2 font-weight-semibold font-normal"
            >
              Save
            </button>
          </div>
        </div>
      </div>
    </Fragment>
  );
};

export interface ActivityModel {
  // date: Date;
  // endDate?: Date;
  // startTime: string;
  // endTime: string;
  timePeriod?: ITimePeriodData;
  location?: string;
  activityType?: string;
  type?: string;
  title?: string;
  modelCode?: string;
  variantCode?: string;
  year?: string;
  vehicleModel?: VehicleModelMinimal;
  vehicleVariant?: VehicleVariantMinimal;
  vehicle?: { _id: string; displayName: string };
  vehicleId?: string;
  salesPerson?: any;
  companyPeople?: any;
  company?: any
  showroom?: { _id: string; title: string };
  leadId?: string;
  note?: string;
  eventId?: string;
  showRoom?: { _id: string; title: string };
  busyEvent?: boolean;
  address?: any;
  customer?: Customer;
  overnightTestdriveEnabled?: boolean;
  users?: Customer[];
  _id?: string;
  activityCategoryData?: {
    _id?:string;
    name?:string
  }
  activityCategory?: string;
  companyId?: string
  activityCategoryId?: string
}

export interface IconButtonModel {
  placeholder: string;
  type: string;
}

export const activityTypeToIconMapper: { [id: string]: IEventMapDetail } = {
  call: {
    icon: "icon-call",
    text: "Call",
    color: "EBFAEF",
    isDraggable: true,
    isSingleDayEvent: true,
    className: "call-event",
  },
  meeting: {
    icon: "icon-users-fill",
    text: "Meeting",
    color: "FDECE2",
    isDraggable: true,
    isSingleDayEvent: true,
    className: "meeting-event",
  },
  deadline: {
    icon: "icon-flag-fill",
    text: "Deadline",
    color: "FFF1F2",
    isDraggable: true,
    isSingleDayEvent: true,
    className: "deadline-event",
  },
  task: {
    icon: "icon-clock",
    text: "Task",
    color: "FDECE2",
    isDraggable: true,
    isSingleDayEvent: true,
    className: "task-event",
  },
  scheduleATestDrive: {
    icon: "icon-drive",
    text: "Schedule a test drive",
    color: "F2F2FF",
    isDraggable: true,
    isSingleDayEvent: true,
    className: "test-drive-event",
  },
  requestACallBack: {
    icon: "icon-call",
    text: "Request a callback",
    color: "EBFAEF",
    isDraggable: true,
    isSingleDayEvent: true,
    className: "request-callback-event",
  },
  showroomAppointment: {
    icon: "icon-building",
    text: "Showroom appointment",
    color: "F5EEFF",
    isDraggable: true,
    isSingleDayEvent: true,
    className: "showroom-appointment-event",
  },
  scheduleASiteVisit: {
    icon: "icon-building",
    text: "Site Visit",
    color: "F5EEFF",
    isDraggable: true,
    isSingleDayEvent: true,
    className: "showroom-appointment-event",
  },
  note: {
    icon: "icon-note",
    text: "Note",
    color: "F7F5CD",
    isDraggable: true,
    isSingleDayEvent: true,
    className: "note-event",
  },
  timeOff: {
    icon: "icon-clock",
    text: "Time Off",
    color: "F0F0F0",
    isDraggable: false,
    isSingleDayEvent: false,
    className: "time-off-event",
  },
  vehicleTimeOff: {
    icon: "icon-clock",
    text: "Vehicle Time Off",
    color: "F0F0F0",
    isDraggable: false,
    isSingleDayEvent: false,
    className: "time-off-event",
  },
  phoneIn: {
    icon: "icon-phone_callback",
    text: "Phone In",
    color: "EBFAEF",
    isDraggable: true,
    isSingleDayEvent: true,
    className: "phone-in-event",
  },
  phoneOut: {
    icon: "icon-phone_forwarded",
    text: "Phone Out",
    color: "EBFAEF",
    isDraggable: true,
    isSingleDayEvent: true,
    className: "phone-out-event",
  },
  emailIn: {
    icon: "icon-emailin",
    text: "Email In",
    color: "EBFAEF",
    isDraggable: true,
    isSingleDayEvent: true,
    className: "email-in-event",
  },
  emailOut: {
    icon: "icon-emailout",
    text: "Email Out",
    color: "EBFAEF",
    isDraggable: true,
    isSingleDayEvent: true,
    className: "email-out-event",
  },
  letterIn: {
    icon: "icon-letterin",
    text: "Letter In",
    color: "EBFAEF",
    isDraggable: true,
    isSingleDayEvent: true,
    className: "letter-in-event",
  },
  letterOut: {
    icon: "icon-letterout",
    text: "Letter Out",
    color: "EBFAEF",
    isDraggable: true,
    isSingleDayEvent: true,
    className: "letter-out-event",
  },
  faxIn: {
    icon: "icon-faxin",
    text: "Fax In",
    color: "EBFAEF",
    isDraggable: true,
    isSingleDayEvent: true,
    className: "fax-in-event",
  },
  faxOut: {
    icon: "icon-faxout",
    text: "Fax Out",
    color: "EBFAEF",
    isDraggable: true,
    isSingleDayEvent: true,
    className: "fax-out-event",
  },
  coldCalling: {
    icon: "icon-coldcalling",
    text: "Cold Calling",
    color: "EBFAEF",
    isDraggable: true,
    isSingleDayEvent: true,
    className: "cold-calling-event",
  },
  todo: {
    icon: "icon-todo",
    text: "Todo",
    color: "EBFAEF",
    isDraggable: true,
    isSingleDayEvent: true,
    className: "todo-event",
  },
};
export interface IEventMapDetail {
  icon: string;
  text: string;
  color: string;
  isDraggable: boolean;
  isSingleDayEvent: boolean;
  className: string;
}
